import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription, interval } from 'rxjs';
import { NotyfService } from '../../../../core/services/notyf.service';
import { CallSendService } from '../../services/call-send.service';
import { ActiveCall, CallResult, WebRtcCallService } from '../../services/webrtc-call.service';
import { CALL_OUTCOME_LABELS, CallOutcome } from '../../models/call-log.model';

const KEYPAD: { digit: string; letters: string }[] = [
  { digit: '1', letters: '' }, { digit: '2', letters: 'ABC' }, { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' }, { digit: '5', letters: 'JKL' }, { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' }, { digit: '8', letters: 'TUV' }, { digit: '9', letters: 'WXYZ' },
  { digit: '*', letters: '' }, { digit: '0', letters: '+' }, { digit: '#', letters: '' }
];

/**
 * The admin panel's own call screen - a floating console (bottom-right,
 * above every page) for the in-browser call WebRtcCallService is running.
 * Mounted once in AppComponent so a call survives navigating between CRM
 * records. Live: contact, our caller id, status, timer, mute/hold/keypad,
 * hang up, notes. Afterwards: outcome + notes saved onto the same call log.
 */
@Component({
  selector: 'app-call-console',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './call-console.component.html',
  styleUrl: './call-console.component.css'
})
export class CallConsoleComponent implements OnInit, OnDestroy {
  readonly keypad = KEYPAD;
  readonly outcomeOptions = Object.entries(CALL_OUTCOME_LABELS) as [CallOutcome, string][];

  call: ActiveCall | null = null;
  elapsed = '0:00';
  minimized = false;
  showKeypad = false;
  dialled = '';
  notes = '';
  outcome: CallOutcome = 'completed';
  saving = false;

  private subs = new Subscription();

  constructor(
    private webRtcCallService: WebRtcCallService,
    private callSendService: CallSendService,
    private notyfService: NotyfService
  ) {}

  ngOnInit (): void {
    this.subs.add(this.webRtcCallService.state$.subscribe((call) => this.onState(call)));
    this.subs.add(interval(1000).subscribe(() => this.tick()));
  }

  ngOnDestroy (): void {
    this.subs.unsubscribe();
  }

  get isLive (): boolean {
    return this.webRtcCallService.isBusy;
  }

  get contactName (): string {
    return this.call?.request.contactName || this.formatNumber(this.call?.request.number ?? '');
  }

  get initials (): string {
    const name = this.call?.request.contactName?.trim();
    if (!name) return '';
    return name.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('');
  }

  get statusLabel (): string {
    switch (this.call?.phase) {
      case 'connecting': return 'Connecting…';
      case 'ringing': return 'Ringing…';
      case 'in_call': return this.call.onHold ? 'On hold' : 'In call';
      case 'ended': return this.resultLabel(this.call.result);
      case 'unavailable': return 'Not connected';
      default: return '';
    }
  }

  hangUp (): void {
    this.webRtcCallService.hangUp();
  }

  toggleMute (): void {
    this.webRtcCallService.toggleMute();
  }

  toggleHold (): void {
    this.webRtcCallService.toggleHold();
  }

  toggleKeypad (): void {
    this.showKeypad = !this.showKeypad;
  }

  press (digit: string): void {
    this.dialled += digit;
    this.webRtcCallService.sendDigit(digit);
  }

  toggleMinimized (): void {
    this.minimized = !this.minimized;
  }

  /** Saves outcome + notes onto the call's log row, then closes. */
  saveAndClose (): void {
    const id = this.call?.callLogId;
    if (!id) {
      this.close();
      return;
    }

    this.saving = true;
    const duration = this.call?.answeredAt && this.call.endedAt
      ? Math.round((this.call.endedAt - this.call.answeredAt) / 1000)
      : null;

    this.callSendService.update(id, {
      outcome: this.outcome,
      duration_seconds: duration,
      notes: this.notes.trim() || null
    }).subscribe({
      next: (res) => {
        this.saving = false;
        this.webRtcCallService.callLogged$.next(res.data);
        this.notyfService.notify('Call saved.', 'success');
        this.close();
      },
      error: (err) => {
        this.saving = false;
        this.notyfService.notify(err?.error?.message ?? 'Could not save the call. Please try again.', 'error');
      }
    });
  }

  close (): void {
    this.webRtcCallService.dismiss();
  }

  /** Fallback while in-browser calling isn't connected: records the call
   *  and hands the number to the agent's Ringotel softphone (tel: link).
   *  Its Call tab then asks for the outcome. */
  useSoftphone (): void {
    const request = this.call?.request;
    if (!request) return;

    this.saving = true;
    this.callSendService.dial(request.crmSource, request.subjectUuid, request.number, 'softphone').subscribe({
      next: (res) => {
        this.saving = false;
        this.webRtcCallService.callLogged$.next(res.data);
        const link = document.createElement('a');
        link.href = `tel:${request.number.replace(/[^\d+]/g, '')}`;
        link.click();
        this.close();
      },
      error: (err) => {
        this.saving = false;
        this.notyfService.notify(err?.error?.message ?? 'Could not start the call. Please try again.', 'error');
      }
    });
  }

  formatNumber (raw: string): string {
    const digits = (raw ?? '').replace(/[^\d]/g, '');
    if (digits.length === 11 && digits.startsWith('61')) {
      return `+61 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
    }
    if (digits.length === 10 && digits.startsWith('04')) {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
    if (digits.length === 10 && digits.startsWith('0')) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)} ${digits.slice(6)}`;
    }
    return raw;
  }

  private onState (call: ActiveCall | null): void {
    const previous = this.call;
    this.call = call;

    if (!call) return;

    // A new call opened - reset the per-call UI.
    if (!previous || previous.request !== call.request) {
      this.minimized = false;
      this.showKeypad = false;
      this.dialled = '';
      this.notes = '';
      this.saving = false;
    }

    if (call.phase === 'ended' && previous?.phase !== 'ended') {
      this.outcome = call.result === 'completed' ? 'completed'
        : call.result === 'busy' ? 'busy'
        : 'no_answer';
      this.minimized = false;
      this.showKeypad = false;
    }

    this.tick();
  }

  private tick (): void {
    const call = this.call;
    if (!call?.answeredAt) {
      this.elapsed = '0:00';
      return;
    }
    const end = call.endedAt ?? Date.now();
    const total = Math.max(0, Math.floor((end - call.answeredAt) / 1000));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = String(total % 60).padStart(2, '0');
    this.elapsed = hours ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
  }

  private resultLabel (result: CallResult | null): string {
    switch (result) {
      case 'completed': return 'Call ended';
      case 'no_answer': return 'No answer';
      case 'busy': return 'Line busy';
      case 'failed': return 'Call failed';
      default: return 'Call ended';
    }
  }
}
